import { type FC, useEffect } from "react";

import Button from "@/design-system/button";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { LoginModalView, languageSelector, useActiveLoginModalContext } from "@/src/contexts";
import type { IMgmRewards } from "@/src/member-get-member/shared/MgmShared.d";
import { browserStatics } from "@/src/shared/BrowserStatic";
import PageLayout from "@/src/shared/page-layout";
import ImageLoader from "@Components/ImageLoader";
import { CommonEventType, MgmEventType, getCdnImage, gotoNativeScreen } from "@Components/Modules/Helpers";
import { HomeLogin, HomeRegistration } from "@Routes/HomepageLinks";

import CashbackNumbers, { type ICashbackNumbersItem } from "../../cashback/LandingPage/CashbackNumbers";
import GoodieAdvantages, { type IGoodieAdvantages } from "../../cashback/LandingPage/GoodieAdvantages";
import PanelSection from "../../shared/panel-section";
import MgmHowItWorks from "../shared/MgmHowItWorks";
import { MgmLandingPageResources } from "./MgmLandingPage.Resources";
import { MgmLandingPageResourcesEn } from "./MgmLandingPage.Resources.En";

import * as portalCss from "../../shared/Portal.module.css";
import * as css from "./MgmLandingPage.module.css";

export type IMemberGetMemberLandingPage = IMgmRewards & {
    cashbackNumbers: ICashbackNumbersItem[];
    goodieAdvantages: IGoodieAdvantages;
    isMobile: boolean;
};

export const MgmLandingPage: FC<IMemberGetMemberLandingPage> = (props) => {
    const { setActiveLoginModal } = useActiveLoginModalContext();
    const { goodieAdvantagesTitle, lpDashboard } = languageSelector(
        MgmLandingPageResources,
        MgmLandingPageResourcesEn,
    );

    useEffect(
        () =>
            sendTelemetryEvent({
                eventName: "cshbck-mgmDshbrdGuest",
                actionType: "View",
            }),
        [],
    );

    const bonusText = lpDashboard.bonusLabel
        .replace("{receiverBonus}", props.receiverBonusAmount)
        .replace("{senderBonus}", props.senderBonusAmount);

    const changeModal = (path: string, modal: LoginModalView, telemetryEvent: string) => {
        gotoNativeScreen(path);
        setActiveLoginModal(modal);
        sendTelemetryEvent({
            eventName: `common-${telemetryEvent}`,
            actionType: "Click",
            param1: MgmEventType.Dashboard,
        });
    };

    return (
        <PageLayout isNewGrid breadcrumbs={props.isMobile && [{ labelKey: "mgm" }]}>
            <div className={css.mgmLandingPageComponent}>
                <div className={`${portalCss.standardContainerV2}`}>
                    <div
                        className={`${css.mainTopComponent} ${portalCss.setSectionColOne} ${portalCss.containerMargins}`}
                    >
                        <h1 dangerouslySetInnerHTML={{ __html: lpDashboard.title }} />

                        <ImageLoader src={getCdnImage(process.env.MGM_MainImg)} />

                        <div className={css.detailsWrapper}>
                            <p>{lpDashboard.recommendedLabel}</p>
                            <p>{bonusText}</p>
                            <p>{lpDashboard.signUpLabel}</p>
                            <Button
                                as="button"
                                size="M"
                                variant="secondary"
                                onClick={() =>
                                    changeModal(HomeLogin, LoginModalView.Login, CommonEventType.login)
                                }
                            >
                                {lpDashboard.loginText}
                            </Button>
                            <Button
                                as="button"
                                size="M"
                                variant="magentaRound"
                                onClick={() =>
                                    changeModal(
                                        HomeRegistration,
                                        LoginModalView.Registration,
                                        CommonEventType.register,
                                    )
                                }
                            >
                                {lpDashboard.registerText}
                            </Button>
                        </div>
                    </div>
                </div>

                <MgmHowItWorks />

                {browserStatics.isMobileApp === false && (
                    <PanelSection title={goodieAdvantagesTitle}>
                        <GoodieAdvantages
                            {...props.goodieAdvantages}
                            telemetryMobileStore={MgmEventType.Dashboard}
                        />
                    </PanelSection>
                )}

                <PanelSection>
                    <CashbackNumbers numbersList={props.cashbackNumbers} />
                </PanelSection>
            </div>
        </PageLayout>
    );
};
